import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { useT, formatNumber } from '../i18n';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function PriceForecastChart({ showTable, priceForecastData }) {
  const t = useT();
  const [hiddenDatasets, setHiddenDatasets] = useState([]);
  const [initialized, setInitialized] = useState(false);

  const colorMapping = {
    '단독다가구': 'purple',
    '연립다세대': 'red',
    '아파트': '#FFDAB9',
  };

  const allDatasets = priceForecastData ? priceForecastData.datasets
    .filter(dataset => dataset.label !== '토지')
    .filter(dataset => dataset.data && dataset.data.some(d => typeof d === 'number' && isFinite(d)))
    .map(dataset => ({
      ...dataset,
      borderColor: colorMapping[dataset.label] || 'black',
      backgroundColor: colorMapping[dataset.label] || 'black',
      fill: false,
    })) : [];

  useEffect(() => {
    if (!initialized && allDatasets.length > 0) {
      setHiddenDatasets(allDatasets.slice(1).map(d => d.label));
      setInitialized(true);
    }
  }, [allDatasets, initialized]);

  if (!priceForecastData) {
    return <div>{t('Loading...')}</div>;
  }

  const visibleDatasets = allDatasets.filter(
    dataset => !hiddenDatasets.includes(dataset.label)
  );

  const data = {
    labels: ['2025', '2026', '2027', '2028', '2029'],
    datasets: visibleDatasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false,
        text: t('AI 예측 5년 후 평당 가격 (단위: 원)'),
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${t(context.dataset.label)}: ${t('{amount}원', { amount: formatNumber(context.raw) })}`;
          },
        },
      },
    },
    scales: {
        x: {
            grid: {
                drawBorder: true,
                borderColor: '#333',
                borderWidth:3,
            },
            ticks: {
                color: '#333',
                font: {
                    size: 10,
                    family: "'Pretendard-SemiBold', sans-serif"
                }
            },
        },
        y: {
            beginAtZero: false,
            grid: {
                drawBorder: true,
                borderColor: '#333',
                borderWidth:3,
            },
            ticks: {
                callback: value => `${formatNumber(value)}`,
                color: '#333',
                font: {
                    size: 8,
                    family: "'Pretendard-SemiBold', sans-serif"
                }
            },
            title: { display: false },
        },
    }
  };

  const tableData = allDatasets
    .map(dataset => {
    const startPrice = dataset.data[0];
    const minPrice = Math.min(...dataset.data);
    const maxPrice = Math.max(...dataset.data);

    const formatChange = (currentPrice, startPrice) => {
        if (currentPrice === startPrice) return t('변동 없음');
        const diff = currentPrice - startPrice;
        const percentage = ((diff / startPrice) * 100).toFixed(1);
        const sign = diff > 0 ? '+' : '';
        return t('{amount}원 ({percent}%)', { amount: `${sign}${formatNumber(diff)}`, percent: `${sign}${percentage}` });
    };

    return {
        label: dataset.label,
        min: {
            price: formatNumber(minPrice),
            change: formatChange(minPrice, startPrice)
        },
        max: {
            price: formatNumber(maxPrice),
            change: formatChange(maxPrice, startPrice)
        }
    };
  });

  const legendFont = {
    fontFamily: "'Pretendard-SemiBold', sans-serif",
    fontSize: '11px'
  };

  const toggleDataset = (label) => {
    setHiddenDatasets(prev =>
      prev.includes(label)
        ? prev.filter(l => l !== label)
        : [...prev, label]
    );
  };

  return (
    <>
      <div className='title' style={{color:'#737373',fontSize:'12px'}}>{t('AI 평당 가격 예측 그래프')}</div>
      <div className="chart_wrap_border">
      <div className="chart_wrap">
      <div style={{ width: '100%', height: '180px' }}>
        <Line options={options} data={data} />
      </div>

      <hr style={{ border: 'none', height: '1px', backgroundColor: '#ccc', margin: '10px 0' }} />

      <div style={{ display: 'flex', justifyContent: 'flex-start', paddingLeft: '5px', gap:'12px' }}>
        {allDatasets.map((dataset, index) => {
          const isHidden = hiddenDatasets.includes(dataset.label);
          return (
            <div
              key={index}
              onClick={() => toggleDataset(dataset.label)}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                textDecoration: isHidden ? 'line-through' : 'none',
                color: isHidden ? '#aaa' : 'black'
              }}
            >
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: isHidden ? '#aaa' : dataset.borderColor,
                marginRight: '5px'
              }}></span>
              <span style={{...legendFont,color:"#737373"}}>{t(dataset.label)}</span>
            </div>
          );
        })}
      </div>

      </div>
      </div>

      {showTable && (
        <div className="ai_value_container" style={{marginTop:'20px'}}>
          <div className='title' style={{color:'#737373',fontSize:'12px'}}>{t('AI 평가 가치')}</div>
          <div className='ai_land_price_div_table' style={{marginTop:'10px'}}>
            <div className='ai_land_price_div_header'>
              <div className='header_cell'>{t('용도')}</div>
              <div className='header_cell'>{t('최저가')}</div>
              <div className='header_cell'>{t('최고가')}</div>
            </div>
            <div className='ai_land_price_div_body'>
              {tableData.map((item, index) => (
                <div className='ai_land_price_div_row' key={index}>
                  <div className='data_cell label_cell'>{t(item.label)}</div>
                  <div className='data_cell price_cell'>
                    {`${item.min.price}`}{t('(원/㎡)')}
                    <div className='price_percent' style={{ color: item.min.change.includes('+') ? '#FF3B30' : (item.min.change.includes('-') ? 'blue' : '#666') }}>
                      {`${item.min.change}`}
                    </div>
                  </div>
                  <div className='data_cell price_cell'>
                    {t('{amount}원', { amount: item.max.price })}
                    <div className='price_percent' style={{ color: item.max.change.includes('+') ? '#FF3B30' : '#666' }}>
                      {`${item.max.change}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PriceForecastChart;